'use client';

import * as React from 'react';

import { LARGE_SCREEN_QUERY, useMediaQuery } from '../../hooks/useMediaQuery';

export interface UseMobileNavAutoCloseOptions {
  /** Whether the mobile navigation drawer is open. */
  open: boolean;
  /** Closes the drawer. */
  onClose: () => void;
  /**
   * Anything that changes on navigation, usually the pathname. The drawer
   * closes when it changes, however the navigation happened (a drawer link,
   * the user menu, a link in the page).
   */
  routeKey?: string;
  /** The query for the desktop layout, where the drawer is not used. */
  desktopQuery?: string;
}

/**
 * Closes the mobile navigation drawer when the route changes or when the
 * viewport moves into the desktop layout.
 *
 * The drawer counterpart of glp-frontend's `useSidebarAutoCollapse`: it acts
 * only on a change (a new route, crossing the breakpoint), never on a plain
 * re-render, so an open drawer is not closed out from under the user.
 */
export function useMobileNavAutoClose({
  open,
  onClose,
  routeKey,
  desktopQuery = LARGE_SCREEN_QUERY,
}: UseMobileNavAutoCloseOptions) {
  const isDesktop = useMediaQuery(desktopQuery);
  const lastRouteKey = React.useRef(routeKey);

  React.useEffect(() => {
    if (lastRouteKey.current === routeKey) return;
    lastRouteKey.current = routeKey;
    if (open) onClose();
  }, [routeKey, open, onClose]);

  React.useEffect(() => {
    if (open && isDesktop) onClose();
  }, [open, isDesktop, onClose]);
}
